// ────────────────────────────────────────────────────────────────────
// DocumentsScreen — the document-numbering tree + revision control
// (workbench roadmap B1/B2). "Store as metadata, render as a tree": packages
// are NOT real folders (see server/models.py Package's docstring) — this
// screen renders the flat GET /api/packages list as a tree client-side, and
// keeps the cross-cutting views (superseded, direction, asset) folders can
// never give.
//
// SCREEN STRUCTURE CHOICE: a NEW sibling screen to IntakeScreen, not an
// extension of it. IntakeScreen's tree is the ASSET hierarchy (Plant->Area->
// Asset, an Explorer-style "my files" metaphor); the package tree is a
// document-NUMBERING hierarchy that is orthogonal to it — the same file can
// be filed to an asset AND a package independently (see intake_files having
// both asset_id and package_id). Overloading IntakeScreen's `Location` union
// with a second, unrelated tree shape would tangle two different mental
// models into one screen's state machine. A sibling screen keeps each tree
// legible on its own; LeftTree's "แพ็กเกจเอกสาร" node (Stage A) now routes
// here instead of into IntakeScreen.
//
// Left = package tree (local to this screen, not LeftTree — see the
// docstring below on why). Right = the file table for whatever's selected
// (a package, or one of the cross-cutting views), with revision control.
// ────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ChevronDown, ChevronRight, FileText, Folder, FolderPlus, Pencil,
         Plus, Trash2, Upload, Layers, ArrowRightLeft } from "lucide-react";
import { apiClient, errMsg, getCachedUser, STAFF_ROLES } from "@/lib/api";
import { FileTabs } from "@/components/files/FileTabs";
import type { IntakeDirection, IntakeFile, Package } from "@/lib/types";
import { RevBadge } from "@/components/RevBadge";

// ── Tree assembly (flat list -> nested, client-side — see module docstring) ──
interface PackageNode extends Package { children: PackageNode[]; depth: number }

function buildTree(rows: Package[]): PackageNode[] {
  const byId = new Map<number, PackageNode>(rows.map((p) => [p.id, { ...p, children: [], depth: 0 }]));
  const roots: PackageNode[] = [];
  for (const node of byId.values()) {
    if (node.parent_id != null && byId.has(node.parent_id)) byId.get(node.parent_id)!.children.push(node);
    else roots.push(node);
  }
  const setDepth = (nodes: PackageNode[], depth: number) => {
    for (const n of nodes) { n.depth = depth; n.children.sort((a, b) => a.number.localeCompare(b.number)); setDepth(n.children, depth + 1); }
  };
  roots.sort((a, b) => a.number.localeCompare(b.number));
  setDepth(roots, 0);
  return roots;
}

/** Every descendant id of `id` (inclusive) — used to keep the "ย้ายไป
 *  package" / reparent pickers from offering a cycle. */
function subtreeIds(rows: Package[], id: number): Set<number> {
  const children = new Map<number, number[]>();
  for (const p of rows) {
    if (p.parent_id != null) children.set(p.parent_id, [...(children.get(p.parent_id) ?? []), p.id]);
  }
  const out = new Set<number>([id]);
  const stack = [...(children.get(id) ?? [])];
  while (stack.length) {
    const cur = stack.pop()!;
    if (out.has(cur)) continue;
    out.add(cur);
    stack.push(...(children.get(cur) ?? []));
  }
  return out;
}

type Loc =
  | { kind: "package"; id: number }
  | { kind: "all" }
  | { kind: "superseded" }
  | { kind: "asset"; id: number };

const DIRECTION_LABEL: Record<IntakeDirection, string> = { in: "รับเข้า", out: "ส่งออก", internal: "ภายใน" };

function fmtWhen(iso: string | null): string {
  if (!iso) return "—";
  const [d] = iso.split("T");
  return d;
}

export default function DocumentsScreen(): JSX.Element {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const assetIdParam = params.get("asset");
  const assetTagParam = params.get("asset_tag");
  const assetId = assetIdParam ? Number(assetIdParam) : null;
  // The two search deep links that land HERE (tests/fixtures/search_deep_links.json):
  //   package hit                       -> /files/packages?package={id}
  //   file filed to a package, no asset -> /files/packages?file={id}
  // Both were emitted, navigated to, and ignored — this screen renders the
  // package tree root regardless, so the reader clicked a hit naming one
  // document or one package and got the whole archive. Same shape as the
  // ?asset=/?asset_tag= pair already read above.
  const packageIdParam = params.get("package");
  const fileIdParam = params.get("file");
  const linkedFileId = fileIdParam && /^\d+$/.test(fileIdParam) ? Number(fileIdParam) : null;

  const isStaff = (STAFF_ROLES as readonly string[]).includes(getCachedUser()?.role ?? "");
  const [loc, setLoc] = useState<Loc>(() =>
    packageIdParam && /^\d+$/.test(packageIdParam)
      ? { kind: "package", id: Number(packageIdParam) }
      : { kind: "all" });
  const [showSuperseded, setShowSuperseded] = useState(false);   // staff only — see toolbar
  const [direction, setDirection] = useState<IntakeDirection | "">("");
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pkgsQ = useQuery({ queryKey: ["packages"], queryFn: () => apiClient.packages(), staleTime: 30_000 });
  const rows = pkgsQ.data ?? [];
  const tree = useMemo(() => buildTree(rows), [rows]);
  const byId = useMemo(() => new Map(rows.map((p) => [p.id, p])), [rows]);

  const currentOnly = !(isStaff && showSuperseded);
  const listParams = useMemo(() => {
    const base: { package_id?: number; asset_id?: number; direction?: IntakeDirection; current_only: boolean } =
      { current_only: currentOnly };
    if (direction) base.direction = direction;
    if (loc.kind === "package") base.package_id = loc.id;
    if (loc.kind === "asset") base.asset_id = loc.id;
    return base;
  }, [loc, direction, currentOnly]);

  const filesQ = useQuery({
    queryKey: ["documents", listParams],
    queryFn: () => apiClient.intakeList(listParams),
    staleTime: 15_000,
  });
  const allFiles: IntakeFile[] = filesQ.data ?? [];
  // "ฉบับที่ถูกแทนที่ทั้งหมด" is a client-side filter over the widened
  // (current_only=false) fetch above — the server already forces
  // current_only=true for non-staff, so this view is naturally empty for them.
  const files = loc.kind === "superseded" ? allFiles.filter((f) => !f.is_current) : allFiles;

  // ── ?file= : land ON the document, not merely in the list that contains it ──
  // Search returns superseded revisions on purpose (routers/search.py filters on
  // deleted_at only), and the table above is fetched current_only by default, so
  // a rev-A hit is not in `files` until the toggle is widened. Widen once, then —
  // and only then — call it a miss, so the notice stays true for a document that
  // really was trashed or belongs to another tenant.
  //
  // The parameter is CONSUMED, not read continuously: the id moves into
  // `markedFileId` and ?file= is dropped from the URL, exactly as
  // IntakeScreen's resolver does. An earlier cut left it in place so a
  // bookmarked link would survive a refresh, and that was wrong — `linkedFileId`
  // then stayed set for the whole visit and this effect re-ran against every
  // folder the reader opened next. Opening a different package made the id
  // "missing" again, which flipped the staff superseded toggle on with nobody
  // touching it and reinstated the untruthful notice about a document that was
  // still sitting in the package they had just left. The highlight is worth one
  // refresh at most; it is not worth an effect that chases the reader around
  // the tree.
  const [markedFileId, setMarkedFileId] = useState<number | null>(null);
  const [linkMiss, setLinkMiss] = useState(false);
  const widenedRef = useRef(false);
  const linkedRowRef = useRef<HTMLTableRowElement | null>(null);
  // A miss notice belongs to the visit that produced it, not to every folder
  // the reader opens afterwards — same rule as IntakeScreen's. Declared BEFORE
  // the resolver below so that within one commit the reset runs first and can
  // never clobber a miss the resolver just set.
  useEffect(() => { setLinkMiss(false); }, [loc]);
  useEffect(() => {
    if (linkedFileId == null) return;
    if (filesQ.isPending || filesQ.isFetching) return;   // still arriving — decide later
    if (allFiles.some((f) => f.id === linkedFileId)) {
      setMarkedFileId(linkedFileId);
      setLinkMiss(false);
    } else if (isStaff && !showSuperseded && !widenedRef.current) {
      widenedRef.current = true;
      setShowSuperseded(true);
      return;                                            // leave ?file= for the re-run
    } else {
      setLinkMiss(true);
    }
    const next = new URLSearchParams(params);
    next.delete("file");
    setParams(next, { replace: true });
  }, [linkedFileId, allFiles, filesQ.isPending, filesQ.isFetching, isStaff, showSuperseded]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // jsdom has no scrollIntoView; the optional call keeps this a no-op there
    // rather than a thrown error inside a render commit.
    linkedRowRef.current?.scrollIntoView?.({ block: "center" });
  }, [markedFileId, files.length]);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["documents"] }); };
  const refreshPackages = () => { qc.invalidateQueries({ queryKey: ["packages"] }); };

  const toggleCollapse = (id: number) =>
    setCollapsed((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // ── package CRUD (staff, inline prompts — house convention, see
  //    IntakeScreen's renameOne/editNote) ──
  const createPackage = async (parentId: number | null) => {
    const number = window.prompt("เลขแพ็กเกจ (เช่น 1200)");
    if (!number || !number.trim()) return;
    const name = window.prompt("ชื่อแพ็กเกจ");
    if (!name || !name.trim()) return;
    setError(null);
    try {
      await apiClient.packageCreate({ number: number.trim(), name: name.trim(), parent_id: parentId ?? undefined });
      refreshPackages();
    } catch (e) { setError(errMsg(e)); }
  };

  const renamePackage = async (p: Package) => {
    const number = window.prompt("เลขแพ็กเกจ", p.number);
    if (number === null) return;
    const name = window.prompt("ชื่อแพ็กเกจ", p.name);
    if (name === null) return;
    setError(null);
    try {
      await apiClient.packagePatch(p.id, { number: number.trim(), name: name.trim() });
      refreshPackages();
    } catch (e) { setError(errMsg(e)); }
  };

  const movePackage = async (p: Package, newParentId: number | null) => {
    setError(null);
    try {
      await apiClient.packagePatch(p.id, { parent_id: newParentId });
      refreshPackages();
    } catch (e) { setError(errMsg(e)); }
  };

  const deletePackage = async (p: Package) => {
    if (!confirm(`ลบแพ็กเกจ "${p.number} ${p.name}"?`)) return;
    setError(null);
    try {
      await apiClient.packageDelete(p.id);
      refreshPackages();
      if (loc.kind === "package" && loc.id === p.id) setLoc({ kind: "all" });
    } catch (e) { setError(errMsg(e)); }
  };

  // ── file actions ──
  const moveFileToPackage = async (f: IntakeFile, newPackageId: number | null) => {
    setError(null);
    try {
      await apiClient.intakeFileToPackage(f.id, {
        package_id: newPackageId, doc_no: f.doc_no, direction: f.direction,
      });
      refresh();
    } catch (e) { setError(errMsg(e)); }
  };

  const reviseFile = async (f: IntakeFile, file: File) => {
    setBusy(`revise-${f.id}`);
    setError(null);
    try {
      await apiClient.intakeRevise(f.id, file);
      refresh();
    } catch (e) {
      // 422 = the old revision isn't a plain number; ask for an explicit one.
      const msg = errMsg(e);
      if (msg.toLowerCase().includes("revision")) {
        const rev = window.prompt(`ระบุเลข revision ใหม่สำหรับ "${f.filename}" (revision เดิม "${f.revision ?? "—"}" ไม่ใช่ตัวเลข)`);
        if (rev && rev.trim()) {
          try {
            await apiClient.intakeRevise(f.id, file, rev.trim());
            refresh();
          } catch (e2) { setError(errMsg(e2)); }
        }
      } else {
        setError(msg);
      }
    } finally {
      setBusy(null);
    }
  };

  const flatPackages = useMemo(() => [...rows].sort((a, b) => a.number.localeCompare(b.number)), [rows]);

  const crumb =
    loc.kind === "all" ? "เอกสารทั้งหมด"
    : loc.kind === "superseded" ? "ฉบับที่ถูกแทนที่ทั้งหมด"
    : loc.kind === "asset" ? `เอกสารของ ${assetTagParam ?? `asset #${loc.id}`}`
    : (byId.get(loc.id) ? `${byId.get(loc.id)!.number} ${byId.get(loc.id)!.name}` : "…");

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden" style={{ background: "var(--paper)" }}>
      {/* The package tree is one lens on the same pile the other two show —
          see FileTabs. This screen stayed its own screen (its docstring above
          explains why two trees must not share one state machine); only the
          navigation was unified. */}
      <FileTabs active="packages" />
      {/* ── Header ── */}
      <div className="px-6 py-3 border-b flex items-center gap-3" style={{ borderColor: "var(--hairline)" }}>
        <div className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".22em", color: "var(--accent-deep)", fontWeight: 600 }}>
          เอกสาร
        </div>
        <div className="font-mono" style={{ fontSize: 12, color: "var(--ink-1)", fontWeight: 600 }}>{crumb}</div>
        {linkMiss && (
          <div data-testid="doc-link-miss" className="font-mono" style={{ fontSize: 11.5, color: "var(--err)" }}>
            ไม่พบเอกสารที่ลิงก์มา — อาจถูกลบ หรืออยู่นอกสิทธิ์ของคุณ
          </div>
        )}
        {error && (
          <div className="ml-auto font-mono px-2 py-1" style={{ fontSize: 10.5, color: "var(--err)", background: "rgba(192,57,43,0.08)", border: "1px solid var(--err)" }}>
            {error}
          </div>
        )}
      </div>

      <div className="flex-1 flex min-h-0">
        {/* ── left: package tree ── */}
        <nav className="w-[260px] shrink-0 border-r overflow-y-auto py-2" style={{ borderColor: "var(--hairline)" }}>
          <button type="button" data-testid="doc-node-all" onClick={() => setLoc({ kind: "all" })}
                  className="w-full flex items-center gap-1.5 px-3 py-1.5 text-left font-mono hover:bg-[rgba(31,78,121,0.05)]"
                  style={{ fontSize: 11.5, color: "var(--ink-2)", background: loc.kind === "all" ? "rgba(var(--accent-rgb) / 0.10)" : undefined }}>
            <Layers size={13} style={{ color: "var(--ink-4)" }} /> เอกสารทั้งหมด
          </button>
          {assetId != null && (
            <button type="button" data-testid="doc-node-asset" onClick={() => setLoc({ kind: "asset", id: assetId })}
                    className="w-full flex items-center gap-1.5 px-3 py-1.5 text-left font-mono hover:bg-[rgba(31,78,121,0.05)]"
                    style={{ fontSize: 11.5, color: "var(--ink-2)", background: loc.kind === "asset" ? "rgba(var(--accent-rgb) / 0.10)" : undefined }}>
              <ArrowRightLeft size={13} style={{ color: "var(--ink-4)" }} /> ทั้งหมดของ {assetTagParam ?? `asset #${assetId}`}
            </button>
          )}
          {isStaff && (
            <button type="button" data-testid="doc-node-superseded" onClick={() => setLoc({ kind: "superseded" })}
                    className="w-full flex items-center gap-1.5 px-3 py-1.5 text-left font-mono hover:bg-[rgba(31,78,121,0.05)]"
                    style={{ fontSize: 11.5, color: "var(--ink-2)", background: loc.kind === "superseded" ? "rgba(var(--accent-rgb) / 0.10)" : undefined }}>
              <Archive size={13} style={{ color: "var(--ink-4)" }} /> ฉบับที่ถูกแทนที่ทั้งหมด
            </button>
          )}

          <div className="flex items-center justify-between px-3 pt-3 pb-1">
            <span className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".2em", color: "var(--ink-4)" }}>แพ็กเกจ</span>
            {isStaff && (
              <button type="button" data-testid="doc-add-root-package" onClick={() => void createPackage(null)} title="เพิ่มแพ็กเกจ">
                <FolderPlus size={13} style={{ color: "var(--accent)" }} />
              </button>
            )}
          </div>
          {pkgsQ.isPending && <div className="px-3 py-1 font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>กำลังโหลด…</div>}
          {!pkgsQ.isPending && tree.length === 0 && (
            <div className="px-3 py-1 font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>ยังไม่มีแพ็กเกจ</div>
          )}
          {tree.map((n) => (
            <PackageRow key={n.id} node={n} loc={loc} setLoc={setLoc} collapsed={collapsed} onToggle={toggleCollapse}
                        isStaff={isStaff} onAddChild={createPackage} onRename={renamePackage}
                        onMove={movePackage} onDelete={deletePackage} allPackages={flatPackages} />
          ))}
        </nav>

        {/* ── right: file table ── */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* toolbar */}
          <div className="px-4 py-2 border-b flex items-center gap-3" style={{ borderColor: "var(--hairline)" }}>
            <div className="flex items-center" style={{ border: "1px solid var(--hairline)" }}>
              {(["", "out", "in", "internal"] as const).map((d, i) => (
                <button key={d || "all"} type="button" data-testid={`doc-direction-${d || "all"}`}
                        onClick={() => setDirection(d)}
                        className="px-2.5 py-1 font-mono uppercase transition"
                        style={{ fontSize: 9.5, letterSpacing: ".06em", borderLeft: i > 0 ? "1px solid var(--hairline)" : "none",
                                 background: direction === d ? "var(--accent)" : "var(--paper-2)",
                                 color: direction === d ? "var(--ink-1)" : "var(--ink-3)", fontWeight: direction === d ? 700 : 400 }}>
                  {d === "" ? "ทั้งหมด" : DIRECTION_LABEL[d]}
                </button>
              ))}
            </div>
            {isStaff && (
              <label className="inline-flex items-center gap-1.5 font-mono cursor-pointer select-none"
                     style={{ fontSize: 10.5, color: "var(--ink-3)" }}>
                <input type="checkbox" data-testid="doc-toggle-superseded" checked={showSuperseded}
                       onChange={(e) => setShowSuperseded(e.target.checked)} />
                แสดงฉบับที่ถูกแทนที่
              </label>
            )}
            <span className="ml-auto font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>
              {files.length} รายการ
            </span>
          </div>

          <div className="flex-1 overflow-auto">
            <table className="w-full font-mono" style={{ fontSize: 12, borderCollapse: "collapse" }}>
              <thead className="sticky top-0 z-10" style={{ background: "var(--paper)" }}>
                <tr style={{ borderBottom: "1px solid var(--hairline)" }}>
                  <Th>Doc No.</Th><Th>ชื่อไฟล์</Th><Th>Rev</Th><Th>ประเภท</Th><Th>ทิศทาง</Th><Th>วันที่</Th><Th right>การกระทำ</Th>
                </tr>
              </thead>
              <tbody>
                {filesQ.isPending && <tr><td colSpan={7} className="text-center py-10" style={{ color: "var(--ink-3)" }}>กำลังโหลด…</td></tr>}
                {!filesQ.isPending && files.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-16" style={{ color: "var(--ink-3)" }}>ไม่มีเอกสาร</td></tr>
                )}
                {files.map((f) => (
                  <FileRow key={f.id} f={f} isStaff={isStaff} busy={busy === `revise-${f.id}`}
                           packages={flatPackages} onMove={moveFileToPackage} onRevise={reviseFile}
                           linked={f.id === markedFileId}
                           rowRef={f.id === markedFileId ? linkedRowRef : undefined} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }): JSX.Element {
  return (
    <th className={`px-3 py-2 font-mono uppercase ${right ? "text-right" : "text-left"}`}
        style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--ink-4)" }}>
      {children}
    </th>
  );
}

function PackageRow({ node, loc, setLoc, collapsed, onToggle, isStaff, onAddChild, onRename, onMove, onDelete, allPackages }: {
  node: PackageNode; loc: Loc; setLoc: (l: Loc) => void;
  collapsed: Set<number>; onToggle: (id: number) => void; isStaff: boolean;
  onAddChild: (parentId: number | null) => void; onRename: (p: Package) => void;
  onMove: (p: Package, newParentId: number | null) => void; onDelete: (p: Package) => void;
  allPackages: Package[];
}): JSX.Element {
  const open = !collapsed.has(node.id);
  const active = loc.kind === "package" && loc.id === node.id;
  const excluded = useMemo(() => subtreeIds(allPackages, node.id), [allPackages, node.id]);
  return (
    <div>
      <div className="group flex items-center gap-1 pr-1 hover:bg-[rgba(31,78,121,0.05)]"
           style={{ paddingLeft: 12 + node.depth * 14, background: active ? "rgba(var(--accent-rgb) / 0.10)" : undefined }}>
        {node.children.length > 0 ? (
          <button type="button" onClick={() => onToggle(node.id)} className="p-0.5">
            {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          </button>
        ) : <span style={{ width: 15 }} />}
        <button type="button" data-testid={`doc-node-package-${node.id}`} onClick={() => setLoc({ kind: "package", id: node.id })}
                className="flex-1 flex items-center gap-1.5 py-1.5 text-left font-mono min-w-0"
                style={{ fontSize: 11.5, color: "var(--ink-2)" }}>
          <Folder size={13} style={{ color: "var(--ink-4)", flexShrink: 0 }} />
          <span className="truncate">{node.number} {node.name}</span>
          {(node.file_count > 0) && <span style={{ color: "var(--ink-4)", fontSize: 10 }}>({node.file_count})</span>}
        </button>
        {isStaff && (
          <span className="hidden group-hover:flex items-center gap-1 shrink-0">
            <button type="button" title="เพิ่มแพ็กเกจย่อย" onClick={() => onAddChild(node.id)}><Plus size={11} style={{ color: "var(--ink-3)" }} /></button>
            <button type="button" title="แก้ไข" onClick={() => onRename(node)}><Pencil size={11} style={{ color: "var(--ink-3)" }} /></button>
            <button type="button" title="ลบ" onClick={() => onDelete(node)}><Trash2 size={11} style={{ color: "var(--err)" }} /></button>
          </span>
        )}
      </div>
      {isStaff && (
        <div style={{ paddingLeft: 12 + node.depth * 14 + 15 }} className="pb-1">
          <select data-testid={`doc-move-package-${node.id}`} value=""
                  onChange={(e) => { const v = e.target.value; onMove(node, v === "" ? null : Number(v)); e.target.value = ""; }}
                  className="font-mono" style={{ fontSize: 9.5, color: "var(--ink-4)", background: "transparent", border: "none" }}>
            <option value="" disabled>ย้ายไปที่…</option>
            <option value="">— ราก —</option>
            {allPackages.filter((p) => !excluded.has(p.id)).map((p) => (
              <option key={p.id} value={p.id}>{p.number} {p.name}</option>
            ))}
          </select>
        </div>
      )}
      {open && node.children.map((c) => (
        <PackageRow key={c.id} node={c} loc={loc} setLoc={setLoc} collapsed={collapsed} onToggle={onToggle}
                    isStaff={isStaff} onAddChild={onAddChild} onRename={onRename} onMove={onMove}
                    onDelete={onDelete} allPackages={allPackages} />
      ))}
    </div>
  );
}

function FileRow({ f, isStaff, busy, packages, onMove, onRevise, linked, rowRef }: {
  f: IntakeFile; isStaff: boolean; busy: boolean; packages: Package[];
  onMove: (f: IntakeFile, newPackageId: number | null) => void;
  onRevise: (f: IntakeFile, file: File) => void;
  linked?: boolean; rowRef?: React.RefObject<HTMLTableRowElement>;
}): JSX.Element {
  return (
    <tr ref={rowRef} data-testid={`doc-file-row-${f.id}`} aria-selected={linked || undefined}
        data-linked={linked ? "true" : undefined}
        style={{ borderBottom: "1px solid var(--hairline)", opacity: f.is_current ? 1 : 0.65,
                 background: linked ? "rgba(var(--accent-rgb) / 0.12)" : undefined,
                 boxShadow: linked ? "inset 3px 0 0 var(--accent)" : undefined }}>
      <td className="px-3 py-2 align-middle" style={{ color: "var(--ink-3)" }}>{f.doc_no ?? "—"}</td>
      <td className="px-3 py-2 align-middle min-w-0">
        <span className="flex items-center gap-1.5 min-w-0">
          <FileText size={13} style={{ color: "var(--ink-4)", flexShrink: 0 }} />
          <span className="truncate" style={{ color: "var(--ink-1)", maxWidth: 260 }}>{f.filename}</span>
        </span>
      </td>
      <td className="px-3 py-2 align-middle">
        <RevBadge revision={f.revision} isCurrent={f.is_current} showSuperseded={isStaff} />
      </td>
      <td className="px-3 py-2 align-middle" style={{ color: "var(--ink-3)" }}>{f.kind}</td>
      <td className="px-3 py-2 align-middle" style={{ color: "var(--ink-3)" }}>{f.direction ? DIRECTION_LABEL[f.direction] : "—"}</td>
      <td className="px-3 py-2 align-middle tabular-nums" style={{ color: "var(--ink-3)" }}>{fmtWhen(f.filed_at ?? f.uploaded_at)}</td>
      <td className="px-3 py-2 align-middle text-right">
        <div className="inline-flex items-center gap-2">
          {f.download_url && (
            <a href={f.download_url} target="_blank" rel="noreferrer" title="ดาวน์โหลด"
               className="font-mono uppercase" style={{ fontSize: 9.5, color: "var(--ink-3)" }}>ดาวน์โหลด</a>
          )}
          {isStaff && f.is_current && (
            <label className="font-mono uppercase cursor-pointer inline-flex items-center gap-1"
                   style={{ fontSize: 9.5, color: busy ? "var(--ink-4)" : "var(--accent-deep)" }}>
              <Upload size={11} /> {busy ? "กำลังอัปโหลด…" : "ออก Revision ใหม่"}
              <input type="file" className="hidden" disabled={busy}
                     data-testid={`doc-revise-input-${f.id}`}
                     onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; if (file) onRevise(f, file); }} />
            </label>
          )}
          {isStaff && (
            <select data-testid={`doc-file-move-package-${f.id}`} defaultValue={f.package_id ?? ""}
                    onChange={(e) => { const v = e.target.value; onMove(f, v === "" ? null : Number(v)); }}
                    className="font-mono" style={{ fontSize: 9.5, color: "var(--ink-3)", background: "var(--paper)", border: "1px solid var(--hairline)" }}>
              <option value="">ย้ายไป package…</option>
              {packages.map((p) => <option key={p.id} value={p.id}>{p.number} {p.name}</option>)}
            </select>
          )}
        </div>
      </td>
    </tr>
  );
}
